import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Canvas as FabricCanvas, Point, type TPointerEventInfo } from 'fabric';
import { isEmpty } from '@witmetrics/utils/misc';
import type { Coords } from '@witmetrics/utils';
import { buildCoords } from '@/utils/dom';
import { useMountEffect } from '@/hooks/useMountEffect';
import { MAX_ZOOM, MIN_ZOOM } from '@/components/viewers/ImageViewer/constants';
import type { SimulationControl } from './types';
import { CONTROLS } from './constants';

export { CONTROLS };

export interface FabricProviderProps extends PropsWithChildren {}

type FabricValue = {
  activeControl: SimulationControl;
  setCanvas: (canvas: HTMLCanvasElement) => FabricCanvas;
  setActiveControl: (control: SimulationControl) => void;
  setPanning: (isPanEnabled: boolean) => void;
  getFabric: () => FabricCanvas | null;
};

const FabricContext = createContext<FabricValue | undefined>(undefined);

export default function FabricProvider({ children }: FabricProviderProps) {
  const fabric = useRef<FabricCanvas | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const lastCoords = useRef<Coords | null>(null);
  const isPanning = useRef(false);
  const isPanEnabled = useRef(true);
  const isPolygonEnabled = useRef(false);
  const [activeControl, setActiveControl] = useState<SimulationControl>(
    CONTROLS.PAN
  );

  useMountEffect(() => {
    return () => {
      fabric.current?.dispose();
    };
  });

  useEffect(() => {
    if (!fabric.current) return;
    if (activeControl === CONTROLS.PAN) {
      isPanEnabled.current = true;
    } else if (activeControl === CONTROLS.POLYGON) {
      isPanEnabled.current = false;
    } else if (activeControl === CONTROLS.PEN) {
      isPanEnabled.current = false;
    }
  }, [activeControl]);

  const setCanvas = (canvas: HTMLCanvasElement) => {
    fabric.current?.dispose();
    canvasRef.current = canvas;
    fabric.current = new FabricCanvas(canvasRef.current, {
      selection: false,
      preserveObjectStacking: true,
      height: canvasRef.current.clientHeight,
      width: canvasRef.current.clientWidth,
      hoverCursor: 'default',
    });
    window.fabricCanvas = fabric.current; // TODO: Remove after testing
    // Inspired by http://fabricjs.com/fabric-intro-part-5
    fabric.current.on('mouse:wheel', handleMouseWheel);
    fabric.current.on('mouse:down', handleMouseDown);
    fabric.current.on('mouse:move', handleMouseMove);
    fabric.current.on('mouse:up', handleMouseUp);
    return fabric.current;
  };

  const handleMove = (deltaX: number, deltaY: number) => {
    if (!fabric.current) return;
    fabric.current.viewportTransform[4] += deltaX;
    fabric.current.viewportTransform[5] += deltaY;
    fabric.current.requestRenderAll();
  };

  const handleZoom = ([x, y]: Coords, delta: number) => {
    if (!fabric.current) return;
    let zoom = fabric.current.getZoom();
    zoom *= 0.999 ** delta;
    if (zoom > MAX_ZOOM) zoom = MAX_ZOOM;
    if (zoom < MIN_ZOOM) zoom = MIN_ZOOM;
    fabric.current.zoomToPoint(new Point(x, y), zoom);
  };

  const handleMouseWheel = (opt: TPointerEventInfo<WheelEvent>) => {
    opt.e.preventDefault();
    opt.e.stopPropagation();
    handleZoom(
      buildCoords(opt.e, canvasRef.current?.getBoundingClientRect()),
      opt.e.deltaY
    );
  };

  const handleMouseDown = (opt: TPointerEventInfo<MouseEvent>) => {
    if (!fabric.current) return;
    if (isPolygonEnabled.current) {
      // eslint-disable-next-line witmetrics/no-multiline-comments
      /* polyPoints.current.push(opt.scenePoint);
      // if (polygonRef.current) fabric.current.remove(polygonRef.current);
      // polygonRef.current = createPolygon(polyPoints.current, {
      //   selectable: false,
      // });
      // fabric.current.add(polygonRef.current);
      // fabric.current.renderAll();
       */
    } else if (isPanEnabled.current) {
      isPanning.current = true;
      lastCoords.current = [opt.e.clientX, opt.e.clientY];
    }
  };

  const handleMouseMove = (opt: TPointerEventInfo) => {
    if (!isPanEnabled.current || !isPanning.current || !lastCoords.current) {
      return;
    }
    const { clientX, clientY } = opt.e as MouseEvent;
    const [lastX, lastY] = lastCoords.current;
    handleMove(clientX - lastX, clientY - lastY);
    lastCoords.current = [clientX, clientY];
  };

  const handleMouseUp = () => {
    if (!fabric.current) return;
    if (isPanning.current) {
      // Call setViewportTransform to recalculate new interaction for all objects
      fabric.current.setViewportTransform(fabric.current!.viewportTransform);
      isPanning.current = false;
    } else if (fabric.current.isDrawingMode) {
      const objects = fabric.current.getObjects();
      if (isEmpty(objects)) return;
      // Last element in array will be the new path, shouldn't be selectable
      objects[objects.length - 1].selectable = false;
    }
  };

  const contextValue = {
    activeControl,
    setCanvas,
    setActiveControl,
    setPanning: (enabled: boolean) => (isPanEnabled.current = enabled),
    getFabric: () => fabric.current,
  };

  return (
    <FabricContext.Provider value={contextValue}>
      {children}
    </FabricContext.Provider>
  );
}

export function useFabric() {
  const context = useContext(FabricContext);
  if (!context) {
    throw Error('useFabric must be used within a FabricProvider');
  }
  return context;
}
